import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleAlert, CheckCircle2, Clock, Filter, FileText, UserCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { exportSegnalazioniReport } from "../lib/exporter";
import { Button } from "../components/ui/button";

const CAT_LABEL = {
  alvo: "Alvo intestinale", alzate: "Alzate", messa_a_letto: "Messa a letto",
  controllo_terapie: "Controllo terapie", non_mangia: "Non mangia", non_beve: "Non beve",
  docce: "Docce", parametri: "Parametri vitali", infettive: "Malattie infettive",
};
const CAT_COLORS = {
  alvo: "#7c3aed", alzate: "#0ea5e9", messa_a_letto: "#6366f1", controllo_terapie: "#ef4444",
  non_mangia: "#f59e0b", non_beve: "#f97316", docce: "#3b82f6", parametri: "#10b981", infettive: "#e11d48",
};
const meseShort = (m) => { try { return new Date(m + "-01T00:00:00").toLocaleDateString("it-IT", { month: "short", year: "2-digit" }); } catch { return m; } };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };
const meseLabel = (m) => { try { const s = new Date(m + "-01T00:00:00").toLocaleDateString("it-IT", { month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return m; } };

function NoteList({ note = [] }) {
  return (
    <div className="mt-1.5 space-y-1">
      {note.map((n, i) => (
        <div key={i} className="text-sm">
          <span>{n.testo}</span>
          <span className="text-[11px] text-muted-foreground ml-2 inline-flex items-center gap-1"><Clock size={10} /> {n.operatore_nome || "—"}{n.ora ? ` · ${n.ora}` : ""}</span>
        </div>
      ))}
    </div>
  );
}

export default function PannelloSegnalazioni() {
  const qc = useQueryClient();
  const { selectedStructure, structures = [], companyLogo, user, can } = useAuth();
  const { activeOperatore, opName } = useFirma();
  const [tab, setTab] = useState("aperte");
  const [soloOggi, setSoloOggi] = useState(false);
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const oggi = ymd(new Date());
  const noStruct = !selectedStructure || selectedStructure === "all";
  const canCorreggi = can("correggi_spunte");
  const strutturaNome = (structures.find((s) => s.id === selectedStructure) || {}).nome || "";
  const closerName = activeOperatore ? opName : ((user?.role === "admin" || user?.role === "super_admin") ? "Admin" : (user?.name || "Admin"));

  const { data: dataAperte } = useQuery({
    enabled: !noStruct,
    queryKey: ["segnalazioni-aperte", selectedStructure, soloOggi ? oggi : "all"],
    queryFn: async () => (await api.get("/segnalazioni/aperte", { params: { structure_id: selectedStructure, ...(soloOggi ? { data: oggi } : {}) } })).data,
  });
  const { data: dataRisolte } = useQuery({
    enabled: !noStruct && tab === "risolte",
    queryKey: ["segnalazioni-risolte", selectedStructure, mese],
    queryFn: async () => (await api.get("/segnalazioni/risolte", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const { data: andamento } = useQuery({
    enabled: !noStruct,
    queryKey: ["segn-andamento", selectedStructure],
    queryFn: async () => (await api.get("/segnalazioni/andamento", { params: { structure_id: selectedStructure, mesi: 6 } })).data,
  });
  const aperte = dataAperte?.segnalazioni || [];
  const risolte = dataRisolte?.segnalazioni || [];
  const cats = andamento?.categorie || [];
  const chartData = (andamento?.mesi || []).map((r) => ({ ...r, name: meseShort(r.mese) }));
  const [filtroCat, setFiltroCat] = useState("");
  const catOptions = Array.from(new Set([...cats, ...aperte.map((s) => s.categoria), ...risolte.map((s) => s.categoria)])).filter(Boolean);
  const aperteF = filtroCat ? aperte.filter((s) => s.categoria === filtroCat) : aperte;
  const risolteF = filtroCat ? risolte.filter((s) => s.categoria === filtroCat) : risolte;
  const chartCats = filtroCat ? [filtroCat] : cats;
  const trendVisible = chartData.some((r) => chartCats.some((c) => (r[c] || 0) > 0));

  const risolvi = async (s) => {
    try { await api.post(`/segnalazioni/${s.id}/chiudi`, { chiuso_da: closerName }); toast.success("Segnalazione risolta"); qc.invalidateQueries({ queryKey: ["segnalazioni-aperte"] }); qc.invalidateQueries({ queryKey: ["segnalazioni-risolte"] }); qc.invalidateQueries({ queryKey: ["segnalazioni"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const scaricaReport = async () => {
    try {
      const { data } = await api.get("/segnalazioni/report", { params: { structure_id: selectedStructure, mese } });
      if (!data.totale) { toast.info("Nessuna segnalazione in questo mese"); return; }
      exportSegnalazioniReport({ mese, strutturaNome, perCategoria: data.per_categoria || {}, logo: companyLogo });
      toast.success("Report PDF generato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore report"); }
  };

  if (noStruct) return <div className="p-8"><h1 className="text-2xl font-head font-bold mb-2">Segnalazioni</h1><p className="text-muted-foreground">Seleziona una struttura dal menu in alto per vedere le segnalazioni.</p></div>;

  return (
    <div className="p-4 sm:p-6 lg:px-10" data-testid="pannello-segnalazioni">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="w-11 h-11 rounded-xl bg-amber-100 grid place-items-center"><CircleAlert className="text-amber-600" size={22} /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-head font-bold">Segnalazioni</h1>
          <p className="text-sm text-muted-foreground">{strutturaNome} · gestisci le segnalazioni aperte e consulta lo storico di quelle risolte</p>
        </div>
      </div>

      {/* Mini grafico andamento ultimi 6 mesi per categoria */}
      <div className="gr-card p-4 mb-4" data-testid="ps-andamento">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="font-head font-bold text-sm">Andamento ultimi 6 mesi <span className="font-normal text-muted-foreground">· {filtroCat ? (CAT_LABEL[filtroCat] || filtroCat) : "segnalazioni per categoria"}</span></h2>
        </div>
        {!trendVisible ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Nessuna segnalazione{filtroCat ? ` di ${CAT_LABEL[filtroCat] || filtroCat}` : ""} negli ultimi 6 mesi.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} className="capitalize" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v, k) => [v, CAT_LABEL[k] || k]} labelStyle={{ fontWeight: 700 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                {chartCats.map((c) => (
                  <Bar key={c} dataKey={c} stackId="s" fill={CAT_COLORS[c] || "#94a3b8"} radius={chartCats[chartCats.length - 1] === c ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {chartCats.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CAT_COLORS[c] || "#94a3b8" }} /> {CAT_LABEL[c] || c}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4 border-b">
        <button onClick={() => setTab("aperte")} data-testid="ps-tab-aperte" className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "aperte" ? "border-amber-500 text-amber-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>Aperte {aperte.length > 0 && <span className="ml-1 text-xs bg-amber-500 text-white rounded-full px-1.5">{aperte.length}</span>}</button>
        <button onClick={() => setTab("risolte")} data-testid="ps-tab-risolte" className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "risolte" ? "border-emerald-500 text-emerald-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>Risolte</button>
      </div>

      {/* Filtro rapido per categoria */}
      {catOptions.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="ps-filtro-cat">
          <span className="text-xs text-muted-foreground font-semibold mr-1">Categoria:</span>
          <button onClick={() => setFiltroCat("")} data-testid="ps-filtro-tutte" className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${!filtroCat ? "bg-foreground text-white border-foreground" : "bg-white hover:bg-secondary"}`}>Tutte</button>
          {catOptions.map((c) => (
            <button key={c} onClick={() => setFiltroCat(c)} data-testid={`ps-filtro-${c}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${filtroCat === c ? "text-white border-transparent" : "bg-white hover:bg-secondary"}`} style={filtroCat === c ? { background: CAT_COLORS[c] || "#64748b" } : {}}>{CAT_LABEL[c] || c}</button>
          ))}
        </div>
      )}

      {tab === "aperte" ? (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-1.5"><span className="text-2xl font-head font-bold text-amber-600 tabnum" data-testid="ps-totale">{aperteF.length}</span><span className="text-xs text-muted-foreground font-semibold">aperte{filtroCat ? " (filtrate)" : ""}</span></div>
            <Button size="sm" variant={soloOggi ? "default" : "outline"} onClick={() => setSoloOggi((v) => !v)} data-testid="ps-oggi"><Filter size={14} className="mr-1" /> {soloOggi ? "Solo oggi" : "Tutte le date"}</Button>
          </div>
          {aperteF.length === 0 ? (
            <div className="gr-card p-12 text-center text-muted-foreground" data-testid="ps-empty">
              <CheckCircle2 className="mx-auto mb-3 text-emerald-400" size={44} /><p className="font-semibold">Nessuna segnalazione aperta</p><p className="text-sm">Tutto sotto controllo{soloOggi ? " per oggi" : ""}{filtroCat ? ` per ${CAT_LABEL[filtroCat] || filtroCat}` : ""}.</p>
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="ps-list">
              {aperteF.map((s) => (
                <div key={s.id} className="gr-card p-4 flex items-start gap-3" data-testid={`ps-item-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{CAT_LABEL[s.categoria] || s.categoria}</span>
                      <span className="font-semibold">{s.ospite_nome || "—"}</span>
                      {s.data && <span className="text-xs text-muted-foreground">· {s.data}</span>}
                      {(s.note || []).length > 1 && <span className="text-[11px] font-bold text-amber-700">· {s.note.length} note</span>}
                    </div>
                    <NoteList note={s.note} />
                  </div>
                  {canCorreggi ? (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 shrink-0" onClick={() => risolvi(s)} data-testid={`ps-risolvi-${s.id}`}><CheckCircle2 size={15} className="mr-1" /> Risolvi</Button>
                  ) : (<span className="text-[11px] text-muted-foreground shrink-0 max-w-[120px] text-right">Serve il permesso per risolvere</span>)}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="h-9 rounded-lg border px-3 text-sm" data-testid="ps-mese" />
            <span className="text-sm text-muted-foreground">{risolteF.length} risolte in {meseLabel(mese)}{filtroCat ? ` · ${CAT_LABEL[filtroCat] || filtroCat}` : ""}</span>
            <Button size="sm" onClick={scaricaReport} className="ml-auto bg-amber-600 hover:bg-amber-700" data-testid="ps-report-pdf"><FileText size={14} className="mr-1" /> Report PDF del mese</Button>
          </div>
          {risolteF.length === 0 ? (
            <div className="gr-card p-12 text-center text-muted-foreground" data-testid="ps-risolte-empty">
              <CheckCircle2 className="mx-auto mb-3 opacity-40" size={44} /><p className="font-semibold">Nessuna segnalazione risolta in {meseLabel(mese)}</p>
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="ps-risolte-list">
              {risolteF.map((s) => (
                <div key={s.id} className="gr-card p-4" data-testid={`ps-risolta-${s.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{CAT_LABEL[s.categoria] || s.categoria}</span>
                    <span className="font-semibold">{s.ospite_nome || "—"}</span>
                    {s.data && <span className="text-xs text-muted-foreground">· {s.data}</span>}
                    <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><UserCheck size={13} /> Risolta da {s.chiusa_da || "—"} · {fmtDT(s.chiusa_il)}</span>
                  </div>
                  <NoteList note={s.note} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
